import { useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "react-bootstrap";
import ReorderableList from "components/ReorderableList";
import AICCRule from "./AICCRule";
import AICCRuleGroupHeader from "./core/AICCRuleGroupHeader";
import AICCRuleBuilderUtils from "utils/AICCRuleBuilderUtils";
import "./ruleBuilder.scss";

export default function AICCRuleBuilder({
  query = [AICCRuleBuilderUtils.defaultQuery()],
  onChangeQuery,
  useFieldGroup = false,
  fieldsWithGroup = [],
  fields = [],
  operators,
  allowValueSource = false,
  allowArithmetic = false,
  isCaseCheckEnable = true,
}) {
  const [t] = useTranslation();
  const containerRef = useRef(null);
  const ruleChangeCallback = useCallback(
    (index, item) => {
      const temp = Array.from(query);

      const foundIndex = temp.findIndex((e) => e.id === item.id);

      if (foundIndex >= 0) {
        temp[foundIndex] = {
          ...temp[foundIndex],
          ...item,
        };
      }
      if (onChangeQuery) {
        onChangeQuery(temp);
      }
    },
    [onChangeQuery, query]
  );

  const finalQuery = useMemo(() => {
    return query.filter((e) => !e.isDeleted);
  }, [query]);
  return (
    <div className="aicc-ruleBuilder p-3 border rounded-3" ref={containerRef}>
      <AICCRuleGroupHeader
        onAdd={() => {
          const temp = Array.from(query);
          temp.splice(query.length, 0, AICCRuleBuilderUtils.defaultQuery());

          if (onChangeQuery) {
            onChangeQuery(temp);
            setTimeout(() => {
              if (containerRef.current) {
                const bottomPos =
                  containerRef.current.getBoundingClientRect().bottom +
                  window.pageYOffset;
                window.scrollTo({
                  top: bottomPos,
                  behavior: "smooth",
                });
              }
            }, 0);
          }
        }}
      />
      <div className="aicc-ruleBuilder_rule-list">
        <ReorderableList
          items={finalQuery}
          renderItem={(item, index) => {
            const isRuleComplete = AICCRuleBuilderUtils.checkQueryValidation(
              item,
              index === finalQuery.length - 1
            );
            return (
              <AICCRule
                allowValueSource={allowValueSource}
                allowArithmetic={allowArithmetic}
                key={item.id}
                data={item}
                useColumnGroup={useFieldGroup}
                columnsWithGroup={fieldsWithGroup}
                columnList={fields}
                operatorList={operators}
                isCaseCheckEnable={isCaseCheckEnable}
                onChange={(rule) => {
                  ruleChangeCallback(index, rule);
                }}
                isRuleComplete={isRuleComplete}
                onRemove={() => {
                  const temp = Array.from(query);

                  const originalIndex = temp.findIndex((e) => e.id === item.id);

                  const previousObjId =
                    index > 0 ? finalQuery[index - 1]?.id : undefined;

                  const mainPreviousObjIndex = temp.findIndex(
                    (ele) => ele.id === previousObjId
                  );

                  if (originalIndex > -1) {
                    if (mainPreviousObjIndex >= 0) {
                      temp[mainPreviousObjIndex].midOperator = "";
                    }
                    if (temp[originalIndex].expressionId !== undefined) {
                      temp[originalIndex].isDeleted = true;
                    } else {
                      temp.splice(originalIndex, 1);
                    }
                  }

                  if (onChangeQuery) {
                    onChangeQuery(temp);
                  }
                }}
              />
            );
          }}
          separator={(item, index) => {
            return index !== finalQuery.length - 1 ? (
              <div className="aicc-ruleBuilder__rule--combinator col-auto g-0 row my-1 justify-content-center position-relative align-items-center gap-3">
                <div className="aicc-ruleBuilder__rule--combinator--divider"></div>
                <div className="w-auto row g-0 gap-2">
                  <Form.Check
                    inline
                    type={"radio"}
                    label={t("aiccRuleBuilder.andCombinatorLabel")}
                    id={`operation-type-and-${item.id}`}
                    name={`operation-type-${item.id}`}
                    checked={item?.midOperator === "and"}
                    className="w-auto fw-bold"
                    onChange={() => {
                      ruleChangeCallback(index, {
                        ...item,
                        midOperator: "and",
                      });
                    }}
                  />
                  <Form.Check
                    inline
                    className="w-auto fw-bold"
                    type={"radio"}
                    label={t("aiccRuleBuilder.orCombinatorLabel")}
                    id={`operation-type-or-${item.id}`}
                    name={`operation-type-${item.id}`}
                    checked={item?.midOperator === "or"}
                    onChange={() => {
                      ruleChangeCallback(index, {
                        ...item,
                        midOperator: "or",
                      });
                    }}
                  />
                </div>
                <div className="aicc-ruleBuilder__rule--combinator--divider"></div>
              </div>
            ) : (
              <></>
            );
          }}
          onOrderChange={(list) => {
            const nextQueryMap = finalQuery.reduce((acc, item, index) => {
              return {
                ...acc,
                [item.id]: item.midOperator ? finalQuery[index + 1]?.id : null,
              };
            }, {});

            const listWithModifiedOperator = list.map((item, index) => {
              if (index === list.length - 1) {
                return { ...item, midOperator: undefined };
              } else {
                const nextQueryId = nextQueryMap[item.id];
                if (list[index + 1].id === nextQueryId) {
                  return item;
                } else {
                  return { ...item, midOperator: "" };
                }
              }
            });
            onChangeQuery(listWithModifiedOperator);
          }}
        />
      </div>
    </div>
  );
}
